<template>
    <UHeader>
        <template #left>
            <NuxtLink to="/" class="flex gap-2 xl:gap-4 items-center min-w-[140px] xl:min-w-[150px]"
                ><UIcon name="i-custom-logo" class="size-7" /><span class="font-bold">Time Tracker</span></NuxtLink
            >
            <UNavigationMenu
                content-orientation="vertical"
                :items="menuItems"
                variant="link"
                class="hidden lg:flex xl:ms-10"
                :ui="{ list: 'xl:gap-2' }"
            />
        </template>

        <template #right>
            <div class="flex xl:gap-5">
                <UColorModeButton />

                <template v-if="!userSession.isAuthenticated">
                    <UButton icon="i-lucide-log-in" color="neutral" variant="ghost" to="/login" class="lg:hidden" />

                    <UButton
                        label="Sign in"
                        color="neutral"
                        variant="outline"
                        to="/login"
                        class="hidden lg:inline-flex"
                    />
                </template>
                <UDropdownMenu
                    v-else-if="userSession.userInfo"
                    :items="[
                        {
                            to: '/profile',
                            label: 'My Profile',
                            icon: 'lucide-settings-2',
                        },
                        {
                            to: '/settings/work-hours',
                            label: 'Settings',
                            icon: 'lucide-settings',
                            class: userSession.isAdmin ? '' : 'hidden',
                        },
                        {
                            label: 'Back to Admin',
                            icon: 'lucide-undo-2',
                            onClick: signInBackAsAdmin,
                            class: userSession.isLoggedInAsAnotherUser ? '' : 'hidden',
                        },
                        {
                            label: 'Log Out',
                            icon: 'lucide-log-out',
                            onClick: logout,
                        },
                    ]"
                    :content="{ align: 'end' }"
                >
                    <UButton color="neutral" variant="ghost" trailing-icon="i-lucide-chevron-down">{{
                        userSession.userInfo.name
                    }}</UButton>
                </UDropdownMenu>
            </div>
        </template>

        <template #body>
            <UNavigationMenu :items="menuItems" orientation="vertical" class="-mx-2.5" />

            <template v-if="!userSession.isAuthenticated">
                <USeparator v-if="menuItems.length" class="my-6" />

                <UButton label="Sign in" color="neutral" variant="subtle" to="/login" block class="mb-3" />
            </template>
        </template>
    </UHeader>
</template>

<script setup lang="ts">
    import type { NavigationMenuItem } from '@nuxt/ui';
    import type { RouteRecordRaw } from 'vue-router';

    const router = useRouter();
    const userSession = useUserSessionStore();
    const auth = useAuth();

    const menuItems = computed(() => {
        return getMenuItems(router.getRoutes());
    });

    function getMenuItems(routes: RouteRecordRaw[], showInHeaderNav?: boolean): NavigationMenuItem[] {
        const filteredRoutes = routes
            .filter((route) => {
                if (showInHeaderNav ?? route.meta?.showInHeaderNav)
                    return auth.isRouteAccessible(route);

                return false;
            })
            .sort(sortRoutes);

        return filteredRoutes.map((route) => ({
            to: route.children?.length ? undefined : { name: route.name },
            label: route.meta?.title as string,
            // We want to check if the current route starts with the path to keep the parent route active when on a child route
            active: router.currentRoute.value.path.startsWith(route.path + '/') || router.currentRoute.value.name === route.name,
            children: route.children ? getMenuItems(route.children, true) : undefined,
        }));
    }

    async function signInBackAsAdmin() {
        if (userSession.isLoggedInAsAnotherUser) await auth.signInBackAsAdmin();
    }

    async function logout() {
        await auth.signOut();
    }
</script>
